import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { StatCard } from '../../../components/StatCard';
import { EmptyState } from '../../../components/EmptyState';
import { useAttendanceStore, type EmployeeReport } from '../../../store/attendanceStore';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CHART_TOOLTIP_ITEM } from '../../../lib/chartColors';
import type { ExportColumn } from '../../../lib/export';
import { AdjustmentStatusBadge, adjustmentLabel, DayStatusBadge, fmtDate, fmtDateTime, fmtMinutes, fmtTime } from './attendanceUi';
import { ExportButton } from './ExportButton';
import type { DateRange } from './ReportRangePicker';

const hours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;

const dayColumns: ExportColumn<EmployeeReport['days'][number]>[] = [
    { header: 'Date', value: (r) => r.date },
    { header: 'Status', value: (r) => r.status },
    { header: 'Work mode', value: (r) => r.workMode },
    { header: 'First in', value: (r) => fmtTime(r.firstCheckIn) },
    { header: 'Last out', value: (r) => fmtTime(r.lastCheckOut) },
    { header: 'Worked hours', value: (r) => hours(r.workedMinutes) },
    { header: 'Expected hours', value: (r) => (r.expectedMinutes != null ? hours(r.expectedMinutes) : '') },
    { header: 'Late minutes', value: (r) => r.lateMinutes || '' },
    { header: 'Early-out minutes', value: (r) => r.earlyMinutes || '' },
    { header: 'Office hours', value: (r) => r.officeHours },
    { header: 'WFH hours', value: (r) => r.wfhHours },
    { header: 'Sessions', value: (r) => r.sessions },
    { header: 'Location', value: (r) => r.locations },
    { header: 'Adjustments', value: (r) => r.adjustmentCount || '' },
];

/**
 * One employee's attendance over a date range: headline metrics, an hours-per-day
 * chart and a full day-by-day table with export. Used for the signed-in user's
 * own report (no employeeId) and for an admin drilling into one person.
 */
export function EmployeeReportView({ range, employeeId, showName, onBack }: {
    range: DateRange;
    employeeId?: string;
    showName?: boolean;
    onBack?: () => void;
}) {
    const { fetchEmployeeReport, today, fetchToday } = useAttendanceStore();
    const [report, setReport] = useState<EmployeeReport | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { if (!today) fetchToday().catch(() => undefined); }, [today, fetchToday]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchEmployeeReport(range.from, range.to, employeeId)
            .then((r) => { if (!cancelled) setReport(r); })
            .catch((e) => { if (!cancelled) toast.error(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range.from, range.to, employeeId, fetchEmployeeReport]);

    const t = report?.totals;
    const expectedHours = today?.day.expectedMinutes ? today.day.expectedMinutes / 60 : null;
    const chart = (report?.days ?? [])
        .filter((d) => d.workedMinutes > 0)
        .map((d) => ({ label: fmtDate(d.date, { day: 'numeric', month: 'short' }), hours: hours(d.workedMinutes) }));

    const base = `attendance-report_${report?.employee.code || report?.employee.name || 'me'}_${range.from}_to_${range.to}`;

    return (
        <div className="space-y-4">
            {(showName || onBack) && (
                <div className="flex items-center gap-3">
                    {onBack && <Button size="sm" variant="ghost" leftIcon={<ArrowLeft className="size-4" />} onClick={onBack}>Back</Button>}
                    {report && (
                        <div>
                            <h2 className="font-display text-section-title text-foreground">{report.employee.name}</h2>
                            <p className="text-xs text-muted-foreground">
                                {[report.employee.code, report.employee.designation || report.employee.department].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                    {fmtDate(range.from, { day: 'numeric', month: 'short', year: 'numeric' })} – {fmtDate(range.to, { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <ExportButton rows={report?.days ?? []} columns={dayColumns} baseName={base} disabled={loading} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Hours worked" value={fmtMinutes(t?.workedMinutes)} hint={`Avg ${fmtMinutes(t?.avgMinutesPerDay)} per day worked`} />
                <StatCard label="Avg per week" value={fmtMinutes(t?.avgMinutesPerWeek)} hint="Across the whole range" />
                <StatCard label="Days present" value={t?.presentDays ?? '—'} hint={`${t?.halfDays ?? 0} half day(s) · ${t?.lateDays ?? 0} late`} />
                <StatCard label="Needs attention" value={t?.missingCheckoutDays ?? '—'} hint={`${t?.adjustments ?? 0} adjustment(s)`} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Absent" value={t?.absentDays ?? '—'} />
                <StatCard label="On leave" value={t?.leaveDays ?? '—'} />
                <StatCard label="Work from home" value={t?.wfhDays ?? '—'} hint="days" />
                <StatCard label="Holiday / week off" value={(t ? t.holidayDays + t.weekOffDays : '—') as number | string} />
            </div>

            <Card>
                <div className="border-b border-border px-4 py-3">
                    <h3 className="font-display text-card-title text-foreground">Hours per day</h3>
                    <p className="text-xs text-muted-foreground">Counted hours only — days without worked time are left out of the chart.</p>
                </div>
                {chart.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground">{loading ? 'Loading…' : 'No worked hours in this period.'}</p>
                ) : (
                    <div className="h-64 px-2 py-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                                <XAxis dataKey="label" stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} width={32} unit="h" />
                                <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} cursor={{ fill: 'hsl(var(--secondary))' }}
                                    formatter={(v: number) => [fmtMinutes(v * 60), 'Worked']} />
                                {expectedHours && <ReferenceLine y={expectedHours} stroke={CHART_AXIS} strokeDasharray="4 4"
                                    label={{ value: `Expected ${expectedHours}h`, position: 'insideTopRight', fill: CHART_AXIS, fontSize: 11 }} />}
                                <Bar dataKey="hours" fill={CHART.blue} radius={[4, 4, 0, 0]} maxBarSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <Card>
                <div className="border-b border-border px-4 py-3"><h3 className="font-display text-card-title text-foreground">Day by day</h3></div>
                {(report?.days.length ?? 0) === 0 ? (
                    <EmptyState className="m-4 border-0" title={loading ? 'Loading…' : 'No attendance recorded'} description="Try another date range." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2 font-semibold">Date</th>
                                    <th className="px-4 py-2 font-semibold">Status</th>
                                    <th className="px-4 py-2 font-semibold">First in</th>
                                    <th className="px-4 py-2 font-semibold">Last out</th>
                                    <th className="px-4 py-2 text-right font-semibold">Worked</th>
                                    <th className="px-4 py-2 font-semibold">Location</th>
                                    <th className="px-4 py-2 text-right font-semibold">Adj.</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {[...(report?.days ?? [])].reverse().map((d) => (
                                    <tr key={d.date} className="hover:bg-secondary/30">
                                        <td className="whitespace-nowrap px-4 py-2 text-foreground">{fmtDate(d.date)}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap items-center gap-1">
                                                <DayStatusBadge status={d.status} />
                                                {d.isLate && <Badge variant="warning">Late {fmtMinutes(d.lateMinutes)}</Badge>}
                                                {d.hasLocationException && <Badge variant="warning">Location</Badge>}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">{fmtTime(d.firstCheckIn)}</td>
                                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">{fmtTime(d.lastCheckOut)}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-foreground">{d.workedMinutes ? fmtMinutes(d.workedMinutes) : '—'}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{d.locations || (d.workMode === 'WFH' ? 'Home' : '—')}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{d.adjustmentCount || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card>
                <div className="border-b border-border px-4 py-3">
                    <h3 className="font-display text-card-title text-foreground">
                        Adjustment history <span className="text-sm font-normal text-muted-foreground">{report?.adjustments.length ?? 0}</span>
                    </h3>
                    <p className="text-xs text-muted-foreground">Regularisation requests in this period, and who approved or rejected each one.</p>
                </div>
                {(report?.adjustments.length ?? 0) === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground">{loading ? 'Loading…' : 'No adjustment requests in this period.'}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2 font-semibold">Date</th>
                                    <th className="px-4 py-2 font-semibold">Type</th>
                                    <th className="px-4 py-2 font-semibold">Requested</th>
                                    <th className="px-4 py-2 font-semibold">Reason</th>
                                    <th className="px-4 py-2 font-semibold">Status</th>
                                    <th className="px-4 py-2 font-semibold">Reviewed by</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {(report?.adjustments ?? []).map((a) => (
                                    <tr key={a.id} className="align-top hover:bg-secondary/30">
                                        <td className="whitespace-nowrap px-4 py-2 text-foreground">{fmtDate(a.date)}</td>
                                        <td className="px-4 py-2 text-foreground">
                                            {adjustmentLabel(a.type)}
                                            {a.source === 'ADMIN' && <span className="ml-1 text-xs text-muted-foreground">(by HR)</span>}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">
                                            {a.requestedCheckIn || a.requestedCheckOut
                                                ? `${fmtTime(a.requestedCheckIn)} → ${fmtTime(a.requestedCheckOut)}`
                                                : a.requestedLocationName || '—'}
                                        </td>
                                        <td className="max-w-xs px-4 py-2 text-muted-foreground">{a.reason || '—'}</td>
                                        <td className="px-4 py-2"><AdjustmentStatusBadge status={a.status} /></td>
                                        <td className="px-4 py-2 text-muted-foreground">
                                            {a.status === 'approved' || a.status === 'rejected' ? (
                                                <>
                                                    <p className="text-foreground">{a.reviewerName || 'A reviewer'}</p>
                                                    {a.reviewedAt && <p className="text-xs text-muted-foreground">{fmtDateTime(a.reviewedAt)}</p>}
                                                    {a.reviewerComment && <p className="mt-0.5 text-xs italic text-muted-foreground">“{a.reviewerComment}”</p>}
                                                </>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">{a.status === 'pending' ? 'Awaiting review' : '—'}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
